/**
 * Template List — shows all templates for a project with completeness bars.
 */
import { useState, useEffect } from "react";
import { FileOutput, ChevronRight, FileText, Clock } from "lucide-react";
import { api } from "../../lib/api.ts";
import { TemplateGenerationFlow } from "./TemplateGenerationFlow.tsx";

interface TemplateListProps {
  projectId: string;
  countryCode: string;
}

export function TemplateList({ projectId, countryCode }: TemplateListProps) {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  useEffect(() => {
    api.getProjectTemplates(projectId)
      .then(setTemplates)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  if (selectedTemplate) {
    return (
      <TemplateGenerationFlow
        projectId={projectId}
        templateId={selectedTemplate}
        countryCode={countryCode}
        onBack={() => setSelectedTemplate(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => <div key={i} className="h-16 bg-surface-tertiary rounded-xl animate-pulse" />)}
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-6 text-center">
        <FileText size={28} className="mx-auto text-text-tertiary mb-3" />
        <div className="text-[15px] font-medium text-text-primary mb-1">No templates yet</div>
        <p className="text-[13px] text-text-secondary max-w-md mx-auto">
          Submission templates are generated from your license data. Fill in the required fields for a license, and its template will appear here ready to generate.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {templates.map((t: any) => {
        const gen = t.last_generated;
        const genDate = gen ? new Date(gen.created_at).toLocaleDateString() : null;
        const genStale = gen && gen.completeness_pct < t.completeness_pct;

        return (
          <button
            key={`${t.template_id}-${t.country_code}`}
            onClick={() => setSelectedTemplate(t.template_id)}
            className="w-full text-left flex items-center gap-3 px-4 py-3 bg-surface border border-border rounded-2xl card-interactive hover:border-border-secondary"
          >
            <FileOutput size={18} className={gen ? "text-brand" : t.can_generate_final ? "text-brand" : "text-text-tertiary"} />
            <div className="flex-1 min-w-0">
              <div className="text-[16px] font-medium truncate">{t.template_name}</div>
              <div className="text-[13px] text-text-tertiary">
                {t.regulator} · {t.fields_filled}/{t.fields_total} fields · {t.docs_uploaded}/{t.docs_total} docs
              </div>
              {/* Last generated badge */}
              {gen && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Clock size={11} className="text-text-tertiary" />
                  <span className="text-[12px] text-text-tertiary">
                    Draft generated {genDate} at {gen.completeness_pct}%
                  </span>
                  {genStale && (
                    <span className="text-[11px] text-warning bg-warning-light px-1.5 py-0.5 rounded-full">
                      New data available
                    </span>
                  )}
                </div>
              )}
              {/* Completeness bar */}
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${t.can_generate_final ? "bg-brand" : "bg-warning"}`}
                    style={{ width: `${t.completeness_pct}%` }}
                  />
                </div>
                <span className={`text-[12px] font-medium ${t.can_generate_final ? "text-brand" : "text-text-tertiary"}`}>
                  {t.completeness_pct}%
                </span>
              </div>
            </div>
            <ChevronRight size={14} className="text-text-tertiary flex-shrink-0" />
          </button>
        );
      })}
    </div>
  );
}
